import { useRegion } from 'common/hooks/region';
import moment from 'moment';
import { usePerDiemParams } from 'pages/PerDiemArchive/params';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Filter, getEmptyReturnAvailable, SmartScreenshotResponse } from 'services/clients/perDiem';
import { Button } from '@mui/material';
import { ModeRadios } from 'pages/PerDiemArchive/components/mode-radios';
import { PerDiemDisputeTable } from 'components/PerDiemDisputeTable/PerDiemDisputeTable';
import { DateRangeControls } from '../../components/controls';
import { DownloadModal, ErrorModal, UpgradeModal } from './download-modal';
import { ReportCard } from './components/ReportCard';
import { StatCard } from './components/StatCard';
import { PerDiemEmptyState } from './components/PerDiemEmptyState';

export const SmartPerDiem: React.FC = () => {
  const { searchParams, setSearchParams } = usePerDiemParams(true);
  const region = useRegion();

  const [openModal, setOpenModal] = React.useState<
    { modal: 'download' | 'upgrade' } | { modal: 'error'; title: string; message: string } | null
  >();

  // Construct our filter once all the values are provided
  const filter: Filter | undefined = React.useMemo(() => {
    const { shippingLine, container, startDate, endDate } = searchParams;
    if (!shippingLine || !container || !startDate || !endDate) {
      return undefined;
    }
    return {
      shippingLine,
      containerType: container,
      startDate: searchParams.startDate.format('YYYY-MM-DD'),
      endDate: searchParams.endDate.format('YYYY-MM-DD'),
      pageSize: searchParams.pageSize,
      page: searchParams.page,
      portCollection: region.code,
    };
  }, [searchParams, region]);

  const [showReport, setShowReport] = React.useState(false);

  const results = useQuery({
    queryKey: ['smartPerDiem', filter],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: ({ signal }) => getEmptyReturnAvailable(filter!, signal),
    enabled: filter && showReport,
    keepPreviousData: true,
  });

  const handleModal = React.useCallback((code: SmartScreenshotResponse['code']) => {
    switch (code) {
      case 'no_credits':
        setOpenModal({ modal: 'upgrade' });
        break;
      case 'error':
      case 'no_screenshot':
        setOpenModal({
          modal: 'error',
          title: 'There was an error retrieving your screenshot',
          message:
            'We apologize for the inconvenience. Please try to download the screenshot again.',
        });
        break;
      default:
        break;
    }
  }, []);

  const minStartDate = React.useMemo(() => {
    return moment(region.smartPerDiemMinDate).startOf('day');
  }, [region]);

  return (
    <>
      {!showReport && (
        <div className="px-16 mb-4">
          <ModeRadios />
        </div>
      )}

      <div className="space-y-8 flex-grow flex flex-col">
        {showReport && filter ? (
          <div
            className="px-16 justify-between flex border-b border-gray-200 pb-6"
            style={{
              boxShadow: '0px 12px 6px -6px rgba(40, 43, 57, 0.05)',
            }}
          >
            <ReportCard
              filter={filter}
              clearSearch={() => {
                setSearchParams({ page: null });
                setShowReport(false);
              }}
              className="w-1/3 lg:w-5/12 mr-3"
            />
            <StatCard
              resultData={results.data}
              setOpenModal={setOpenModal}
              className="w-2/3 lg:w-7/12 ml-3"
              reportFilter={filter}
            />
          </div>
        ) : (
          <>
            <div
              className="flex items-end space-x-6 px-16 border-b border-gray-200 pb-6"
              style={{
                boxShadow: '0px 12px 6px -6px rgba(40, 43, 57, 0.05)',
              }}
            >
              <DateRangeControls
                className="flex-grow"
                minStartDate={minStartDate}
                withDefaults
                containersToShow={["20'", "40'", '40HC', '45']}
              />
              <Button
                variant="contained"
                color="secondary"
                disabled={!filter}
                onClick={() => setShowReport(true)}
                className="rounded-lg"
                sx={{
                  minHeight: 58,
                  minWidth: 180,
                  width: 224,
                  fontSize: 16,
                  letterSpacing: 0,
                }}
              >
                Create report
              </Button>
            </div>
            <div className="mt-3">
              <PerDiemEmptyState />
            </div>
          </>
        )}

        {showReport && filter && (
          <>
            <DownloadModal
              open={openModal?.modal === 'download'}
              filter={filter}
              onClose={() => setOpenModal(null)}
              feature="smartPerDiem"
            />
            <div className="px-16 pb-16 min-h-[400px]  flex-grow space-y-4">
              <h3 className="font-medium">Details</h3>
              <PerDiemDisputeTable
                data={results.data}
                loading={results.isPreviousData}
                onModal={handleModal}
                setPageSize={(pageSize) => setSearchParams({ pageSize })}
                pageSize={searchParams.pageSize}
                setPage={(page) => {
                  setSearchParams({ page: page + 1 });
                }}
                page={searchParams.page - 1}
              />
            </div>
          </>
        )}
        <UpgradeModal open={openModal?.modal === 'upgrade'} onClose={() => setOpenModal(null)} />
        <ErrorModal
          open={openModal?.modal === 'error'}
          // eslint-disable-next-line
          // @ts-ignore
          title={openModal?.title}
          // eslint-disable-next-line
          // @ts-ignore
          message={openModal?.message}
          onClose={() => setOpenModal(null)}
        />
      </div>
    </>
  );
};
SmartPerDiem.displayName = 'PerDiemArchive.SmartPerDiem';
